import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Droplets, Stethoscope, Home as HomeIcon, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import CampaignDetailModal from './CampaignDetailModal';

const iconMap = {
  Droplets,
  Stethoscope,
  Home: HomeIcon
};

const CampaignHighlights = ({ campaigns }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState(null);

  const slides = campaigns.map((c) => ({
    ...c,
    Icon: iconMap[c.icon] || HomeIcon
  }));

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  useEffect(() => {
    if (slides.length <= 1 || isPaused) return;
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [slides.length, isPaused]);

  return (
    <div
      className="relative max-w-5xl mx-auto"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slide) => {
            const Icon = slide.Icon;
            return (
              <div key={slide.id} className="min-w-full px-2">
                <Card
                  className="border-none shadow-2xl bg-white cursor-pointer transition-all duration-300 hover:-translate-y-1 group"
                  onClick={() => setActiveCampaign(slide)}
                >
                  <CardContent className="p-8 md:p-12">
                    <div className="flex flex-col items-center text-center">
                      <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-sand-beige flex items-center justify-center mb-6 shadow-inner group-hover:bg-warm-amber/20 transition-colors duration-300">
                        <Icon className="w-10 h-10 md:w-12 md:h-12 text-warm-amber" />
                        
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold font-heading text-forest-green mb-2">
                        {slide.title}
                      </h3>
                      <p className="text-warm-amber font-semibold italic mb-6 text-base md:text-lg">
                        {slide.tagline}
                      </p>
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed max-w-2xl mb-6">
                        {slide.summary}
                      </p>
                      <span className="inline-flex items-center text-warm-amber font-semibold text-sm md:text-base">
                        Read full story
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <Button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white hover:bg-warm-amber text-forest-green hover:text-white rounded-full p-2 shadow-lg transition-all z-10"
            size="icon"
            aria-label="Previous campaign"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>

          <Button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white hover:bg-warm-amber text-forest-green hover:text-white rounded-full p-2 shadow-lg transition-all z-10"
            size="icon"
            aria-label="Next campaign"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>

          <div className="flex justify-center gap-2 mt-6">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                onClick={() => setCurrentSlide(index)}
                className={`h-3 rounded-full transition-all ${
                  index === currentSlide
                    ? 'bg-warm-amber w-8'
                    : 'bg-gray-300 hover:bg-gray-400 w-3'
                }`}
                aria-label={`Go to campaign ${index + 1}: ${slide.title}`}
              />
            ))}
          </div>
        </>
      )}

      <CampaignDetailModal
        campaign={activeCampaign}
        open={!!activeCampaign}
        onOpenChange={(open) => {
          if (!open) setActiveCampaign(null);
        }}
      />
    </div>
  );
};

export default CampaignHighlights;
